import { IContent } from '@/@types/cms';
import CreditDonut from '@/components/ui/CreditTracker/snippets/CreditDonut';
import { FC, useMemo, useState } from 'react';
import Text from '@/components/cms/Text';
import Link from '@/components/ui/Link';
import { formatDate } from '@/components/ui/CreditTracker/util/trackerDateHelpers';
import {
  defaultDonutColors,
  warningDonutColors
} from '@/components/ui/CreditTracker/util/constants';
import { TooltipBlockProps } from '../../Tooltip/TooltipBlock';
import { ConvertExpandedContent } from '@/utils/helpers';
import ComplexTooltip from '@/components/ui/Tooltip/ComplexTooltip';
import Icon from '@/components/ui/Icon';
import RichText from '@/components/cms/RichText';
import Requirements from './Requirements';
import { CreditPlanMiniTrackerBaseProps } from './constants';
import { checkDeadlineApproaching } from '@/components/ui/CreditTracker/util/TrackerHelpers';
import classnames from 'classnames';
import { getTrackerUrl } from '@/components/ui/CreditPlan/util/CreditPlanHelpers';

interface CreditPlanMiniTrackerPersonalizedProps extends CreditPlanMiniTrackerBaseProps {
  deadline?: Date;
  creditsNeededTooltip?: IContent;
  jurisdictionKey?: string;
  onPrimaryLinkClick?: () => void;
}

const CreditPlanMiniTrackerPersonalized: FC<CreditPlanMiniTrackerPersonalizedProps> = ({
  creditsCounted,
  creditsEarned,
  creditsRequired,
  showDonut,
  allOtherCreditsTooltip,
  creditBreakdownCreditTypes,
  jurisdictionMessage,
  deadline,
  primaryLink,
  onPrimaryLinkClick,
  jurisdictionKey,
  creditsNeededTooltip
}) => {
  const [contentContainer, setContentContainer] = useState<HTMLDivElement | null>(null);

  const isWarningDeadline = useMemo(
    () => !!deadline && checkDeadlineApproaching(deadline),
    [deadline]
  );

  const creditsNeededText = useMemo(() => {
    const diff = Math.max(creditsRequired - creditsCounted, 0);
    const creditsText = diff == 1 ? 'credit' : 'credits';
    return `${diff} ${creditsText} needed`;
  }, [creditsCounted, creditsRequired]);

  const trackerUrl = useMemo(
    () => getTrackerUrl(primaryLink?.href, jurisdictionKey),
    [jurisdictionKey, primaryLink?.href]
  );

  const creditsNeededTooltipComponent = useMemo(() => {
    const tooltipContent = ConvertExpandedContent<TooltipBlockProps>(creditsNeededTooltip ?? {});

    return (
      (!!tooltipContent?.title || !!tooltipContent?.copy || !!tooltipContent?.link) && (
        <ComplexTooltip>
          <ComplexTooltip.Trigger
            aria-label="Needed credits explanation"
            className="ml-2 text-gray-dark hover:text-gray-darker focus:text-gray-darker"
          >
            <Icon name="info" size="small" />
          </ComplexTooltip.Trigger>
          <ComplexTooltip.Content customContainer={contentContainer}>
            <div>
              {!!tooltipContent.title && (
                <RichText className="text-2 mb-1 font-bold" content={tooltipContent.title} />
              )}
              {!!tooltipContent.copy && (
                <RichText className="text-2" content={tooltipContent.copy} />
              )}
              {!!tooltipContent.link && (
                <Link
                  href={tooltipContent.link?.href}
                  text={tooltipContent.link?.text}
                  title={tooltipContent.link?.title}
                  target={tooltipContent.link?.target}
                />
              )}
            </div>
          </ComplexTooltip.Content>
        </ComplexTooltip>
      )
    );
  }, [contentContainer, creditsNeededTooltip]);

  return (
    <div className="flex flex-col items-start justify-center rounded-lg border border-silver">
      <div className="flex w-full flex-row items-center justify-start rounded-t-lg bg-gray-lightest p-6">
        {!!showDonut && (
          <CreditDonut
            creditsCounted={creditsCounted}
            creditsRequired={creditsRequired}
            creditsEarned={creditsEarned}
            donutColors={isWarningDeadline ? warningDonutColors : defaultDonutColors}
            donutSize="sm"
            showCounter={false}
            variant="ai"
            className="mb-0 mr-6"
          />
        )}
        <div className="flex w-full flex-col flex-nowrap items-start justify-between md:flex-row md:items-center">
          <div className="flex flex-col items-start justify-center">
            {!!deadline && (
              <Text
                className={classnames('label mb-1 not-italic tracking-normal text-gray-dark', {
                  'text-orange': isWarningDeadline
                })}
              >
                Compliance deadline: {formatDate(deadline)}
              </Text>
            )}
            <Text className="text-1-medium whitespace-nowrap">
              {creditsCounted} counted / {creditsRequired} required
            </Text>
            <div className="flex flex-row items-center justify-start">
              <Text className="text-1-medium">
                <span className="bg-gradient-full bg-[length:200%_100%] bg-clip-text bg-right text-transparent">
                  {creditsNeededText}
                </span>
              </Text>
              {creditsNeededTooltipComponent}
            </div>
          </div>
          {!!primaryLink && !!trackerUrl && (
            <Link
              {...primaryLink}
              onClick={event => {
                primaryLink.onClick?.(event);
                onPrimaryLinkClick?.();
              }}
              href={trackerUrl}
              variant="link-with-icon"
              icon="link-out"
              className="fixed-size-label mt-2 font-bold tracking-normal md:mt-0 lg:font-medium"
            />
          )}
        </div>
        <div ref={setContentContainer} />
      </div>
      <Requirements
        creditBreakdownCreditTypes={creditBreakdownCreditTypes}
        allOtherCreditsTooltip={allOtherCreditsTooltip}
        className="w-full rounded-b-lg p-6"
        jurisdictionMessage={jurisdictionMessage}
      />
    </div>
  );
};
export default CreditPlanMiniTrackerPersonalized;
